import * as React from "react";
import useKBConnection from "../hooks/useKBConnection";
import useArticles from "../hooks/useArticles";
import { ArticleStatListItem } from "../types";
import FilterModal from "./FilterModal";
import useTopics from "../hooks/useTopics";
import { useTags } from "../hooks/useTags";
import useAdminUsers from "../hooks/useAdminUsers";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";

function getHelpfulPercentage(article: ArticleStatListItem) {
  if (!article.voteCount || article.voteCount === 0) {
    return "0%";
  }
  return `${Math.round(((article.helpfulCount ?? 0) / article.voteCount) * 100)}%`;
}

function formatDate(isoDate: string | null | undefined) {
  if (!isoDate) return "N/A";
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "N/A";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return "N/A";
  }
}

function getStatusBadge(isPublished: boolean, visibility: string) {
  if (!isPublished) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        Draft
      </span>
    );
  }
  if (visibility === "Internal") {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        Internal
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      Public
    </span>
  );
}

export default function Index() {
  const { connected, kbStatus, forceConnect } = useKBConnection();
  const [connectingKb, setConnectingKb] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [showFilterModal, setShowFilterModal] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [searchEnabled, setSearchEnabled] = React.useState(false);
  const [reindexingId, setReindexingId] = React.useState<string | null>(null);
  const [lastReindexAt, setLastReindexAt] = React.useState<string | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [bulkReindexing, setBulkReindexing] = React.useState(false);
  const { topics } = useTopics();
  const { tags } = useTags();
  const { adminUsers } = useAdminUsers();
  const toastAlert = useToastAlert();
  const api = useApi();

  const { articles, loadingArticles, searchError, filterState, setFilterState } =
    useArticles();

  React.useEffect(() => {
    api.admin.get("api/kb/search/status")
      .then(r => r.json())
      .then(j => {
        setSearchEnabled(j?.enabled === true);
        setLastReindexAt(j?.lastReindexAt ?? null);
      })
      .catch(() => {});
  }, []);

  async function reindexArticle(article: ArticleStatListItem) {
    if (reindexingId !== null) return;
    setReindexingId(article.id);
    try {
      const r = await api.admin.post(`api/kb/search/article/${article.id}/reindex`, {});
      const json = await r.json();
      if (json?.success) {
        toastAlert.displayToast(Severity.Success, json.message ?? "Article pushed to index");
      } else {
        toastAlert.displayToast(Severity.Warning, json?.message ?? "Could not index article");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to reindex article");
    } finally {
      setReindexingId(null);
    }
  }

  function isStale(article: ArticleStatListItem): boolean {
    if (!lastReindexAt || !article.isPublished || article.visibility !== "Public") return false;
    try { return new Date(article.updatedAt) > new Date(lastReindexAt); } catch { return false; }
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    const indexable = articles.filter(a => a.isPublished && a.visibility === "Public").map(a => a.id);
    if (indexable.every(id => selectedIds.has(id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(indexable));
    }
  }

  async function bulkReindex() {
    if (selectedIds.size === 0 || bulkReindexing) return;
    setBulkReindexing(true);
    let ok = 0, fail = 0;
    for (const id of Array.from(selectedIds)) {
      try {
        const r = await api.admin.post(`api/kb/search/article/${String(id)}/reindex`, {});
        const j = await r.json();
        if (j?.success) ok++; else fail++;
      } catch { fail++; }
    }
    setBulkReindexing(false);
    setSelectedIds(new Set());
    if (fail === 0) {
      toastAlert.displayToast(Severity.Success, `${ok} article${ok !== 1 ? "s" : ""} pushed to search index`);
    } else {
      toastAlert.displayToast(Severity.Warning, `${ok} indexed, ${fail} failed — check server logs`);
    }
  }

  React.useEffect(() => {
    setSearchTerm(filterState.searchTerm);
  }, [filterState]);

  function copyLink(article: ArticleStatListItem) {
    if (!article.url) {
      toastAlert.displayToast(Severity.Warning, "No public URL available for this article");
      return;
    }
    navigator.clipboard.writeText(article.url).then(() => {
      setCopiedId(article.id);
      toastAlert.displayToast(Severity.Success, "Link copied to clipboard");
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      toastAlert.displayToast(Severity.Error, "Failed to copy link");
    });
  }

  if (connected === null) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (connected === false) {
    // Regular customers with no KB linked → send them to setup to create one
    if (kbStatus?.stage === "link" && !kbStatus?.isVelaroAdmin) {
      window.location.href = "/kb/setup";
      return null;
    }

    // Velaro admin diagnostic panel (config error, wrong DB, etc.)
    const isConfigError = kbStatus?.stage === "config";
    const canForceConnect = kbStatus?.isVelaroAdmin &&
      (kbStatus?.stage === "link" || kbStatus?.stage === "tenant");

    async function handleForceConnect() {
      setConnectingKb(true);
      const result = await forceConnect();
      setConnectingKb(false);
      if (result.success) {
        toastAlert.displayToast(Severity.Success, result.message);
        window.location.reload();
      } else {
        toastAlert.displayToast(Severity.Error, result.message);
      }
    }

    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <i className="fa fa-triangle-exclamation text-amber-500 text-xl mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-amber-900 mb-1">Knowledge Base Not Connected</h2>
              <p className="text-sm text-amber-800 mb-3">{kbStatus?.error ?? "KB is unavailable."}</p>

              {kbStatus && (
                <div className="bg-white rounded border border-amber-200 p-3 text-xs font-mono text-slate-600 mb-4 space-y-1">
                  {kbStatus.stage && <div><span className="text-slate-400">stage:</span> {kbStatus.stage}</div>}
                  {kbStatus.siteId && <div><span className="text-slate-400">siteId:</span> {kbStatus.siteId}</div>}
                  {kbStatus.server && <div><span className="text-slate-400">server:</span> {kbStatus.server}</div>}
                  {kbStatus.database && <div><span className="text-slate-400">database:</span> {kbStatus.database}</div>}
                  {kbStatus.linkedTenantId && <div><span className="text-slate-400">tenant:</span> {kbStatus.linkedTenantId}</div>}
                  {kbStatus.isAlternate !== undefined && <div><span className="text-slate-400">mode:</span> {kbStatus.isAlternate ? "alternate (staging KB)" : "primary (production KB)"}</div>}
                  {kbStatus.articleCount !== undefined && <div><span className="text-slate-400">articles in DB:</span> {kbStatus.articleCount}</div>}
                </div>
              )}

              {isConfigError && (
                <div className="text-sm text-amber-900 bg-amber-100 rounded p-3">
                  <strong>Action required:</strong> Set the <code className="bg-amber-200 px-1 rounded">KnowledgeBaseConnectionString</code> App Setting in the{" "}
                  <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal</a>{" "}
                  for this App Service to the production KB connection string.
                </div>
              )}

              {canForceConnect && (
                <button
                  type="button"
                  disabled={connectingKb}
                  onClick={handleForceConnect}
                  className="mt-3 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <i className={`fa ${connectingKb ? "fa-circle-notch fa-spin" : "fa-plug"} text-xs`} />
                  {connectingKb ? "Connecting…" : "Force Connect to Production KB"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">
              Knowledge Base Articles
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage articles for your customer-facing knowledge base and bot training
            </p>
            {kbStatus && (
              <p className="text-xs text-slate-400 mt-1 font-mono">
                {kbStatus.database} · tenant {kbStatus.linkedTenantId?.slice(0,8)}… · {kbStatus.articleCount ?? "?"} articles in DB
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {searchEnabled && selectedIds.size > 0 && (
              <button
                type="button"
                disabled={bulkReindexing}
                onClick={bulkReindex}
                className="px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 flex items-center gap-1.5"
              >
                <i className={`fa ${bulkReindexing ? "fa-circle-notch fa-spin" : "fa-arrow-rotate-right"} text-xs`} />
                {bulkReindexing ? "Indexing…" : `Reindex Selected (${selectedIds.size})`}
              </button>
            )}
            <SecondaryBlueButton
              label="Filter"
              onClick={() => setShowFilterModal(true)}
            />
            <PrimaryBlueButton
              label="New Article"
              onClick={() => {
                window.location.href = "/kb/articles/create";
              }}
            />
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setFilterState({ ...filterState, searchTerm });
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1 max-w-md">
              <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search articles by title, content, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Search
            </button>
            {filterState.searchTerm && (
              <button
                type="button"
                className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                onClick={() => {
                  setSearchTerm("");
                  setFilterState({ ...filterState, searchTerm: "" });
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Search error banner */}
        {searchError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
            <strong>KB query error:</strong> <code className="ml-1">{searchError}</code>
          </div>
        )}

        {/* Articles Table */}
        <div className="bg-white rounded-lg border shadow-sm">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b text-xs font-medium text-slate-500 uppercase tracking-wider rounded-t-lg">
            <div className="col-span-5 flex items-center gap-2">
              {searchEnabled && (
                <input
                  type="checkbox"
                  className="rounded border-slate-300 cursor-pointer"
                  title="Select all indexable articles"
                  aria-label="Select all indexable articles"
                  checked={articles.filter(a => a.isPublished && a.visibility === "Public").length > 0 &&
                    articles.filter(a => a.isPublished && a.visibility === "Public").every(a => selectedIds.has(a.id))}
                  onChange={toggleSelectAll}
                />
              )}
              Article
            </div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-center">Views</div>
            <div className="col-span-1 text-center">Helpful</div>
            <div className="col-span-1 text-center">Updated</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Loading overlay for existing articles */}
          {articles.length > 0 && loadingArticles && (
            <div className="relative">
              <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            </div>
          )}

          {/* Initial loading */}
          {articles.length === 0 && loadingArticles && (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          )}

          {/* Article Rows */}
          <div className="divide-y max-h-[calc(100vh-320px)] overflow-y-auto">
            {articles.map((article) => (
              <div
                key={article.id}
                className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-50 transition-colors"
              >
                {/* Title & Topic */}
                <div className="col-span-5 min-w-0 flex items-start gap-2">
                  {searchEnabled && article.isPublished && article.visibility === "Public" && (
                    <input
                      type="checkbox"
                      className="mt-0.5 flex-shrink-0 rounded border-slate-300 cursor-pointer"
                      checked={selectedIds.has(article.id)}
                      onChange={() => toggleSelect(article.id)}
                      aria-label={`Select ${article.title}`}
                    />
                  )}
                  {searchEnabled && !(article.isPublished && article.visibility === "Public") && (
                    <span className="mt-0.5 flex-shrink-0 w-4" />
                  )}
                  <div className="min-w-0">
                    <a
                      href={`/kb/articles/edit/${article.id}`}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate block"
                    >
                      {article.title || "Untitled"}
                    </a>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">
                      {article.topicName || "No Topic"}
                      {article.description && (
                        <span> &middot; {article.description}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="col-span-2 flex items-center gap-1.5 flex-wrap">
                  {getStatusBadge(article.isPublished, article.visibility)}
                  {article.enableBotResponse && (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
                      title={article.botSummary ? "Bot response ready" : "Bot enabled but no summary"}
                    >
                      <i className="fa fa-robot text-[10px] mr-0.5" />
                      Bot
                    </span>
                  )}
                  {searchEnabled && isStale(article) && (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
                      title="This article was updated after the last search index sync — click the sync icon to push it to the search index"
                    >
                      <i className="fa fa-clock text-[10px] mr-0.5" />
                      Not Indexed
                    </span>
                  )}
                </div>

                {/* Views */}
                <div className="col-span-1 text-center">
                  <span className="text-sm font-medium text-slate-700">
                    {(article.hitCount ?? 0).toLocaleString()}
                  </span>
                </div>

                {/* Helpful */}
                <div className="col-span-1 text-center">
                  <span className="text-sm text-slate-600">
                    {getHelpfulPercentage(article)}
                  </span>
                </div>

                {/* Updated */}
                <div className="col-span-1 text-center">
                  <span className="text-xs text-slate-500">
                    {formatDate(article.updatedAt)}
                  </span>
                </div>

                {/* Actions */}
                <div className="col-span-2 flex justify-end gap-1">
                  {/* Copy Link */}
                  {article.isPublished && article.url && (
                    <button
                      className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                      onClick={() => copyLink(article)}
                      title="Copy article link"
                      aria-label="Copy article link"
                    >
                      <i
                        className={`fa ${
                          copiedId === article.id ? "fa-check" : "fa-link"
                        } text-sm`}
                      />
                    </button>
                  )}

                  {/* Open in new tab */}
                  {article.isPublished &&
                    article.visibility === "Public" &&
                    article.url && (
                      <a
                        className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open article in new tab"
                        aria-label="Open article in new tab"
                      >
                        <i className="fa fa-external-link text-sm" />
                      </a>
                    )}

                  {/* Push to search index */}
                  {searchEnabled && article.isPublished && article.visibility === "Public" && (
                    <button
                      className="p-1.5 text-slate-400 hover:text-purple-600 rounded hover:bg-purple-50 transition-colors disabled:opacity-40"
                      onClick={() => reindexArticle(article)}
                      disabled={reindexingId === article.id}
                      title={article.enableBotResponse ? "Push to search index (bot + public search)" : "Push to public search index"}
                      aria-label="Push article to search index"
                    >
                      <i className={`fa ${reindexingId === article.id ? "fa-circle-notch fa-spin" : "fa-arrow-rotate-right"} text-sm`} />
                    </button>
                  )}

                  {/* Edit */}
                  <a
                    className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                    href={`/kb/articles/edit/${article.id}`}
                    title="Edit article"
                    aria-label="Edit article"
                  >
                    <i className="fa fa-pencil text-sm" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {!loadingArticles && articles.length === 0 && (
            <div className="text-center py-16 px-4">
              <i className="fa fa-book text-4xl text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                No articles found
              </h3>
              <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
                {filterState.searchTerm
                  ? `No articles match "${filterState.searchTerm}". Try a different search term or adjust your filters.`
                  : "Get started by creating your first knowledge base article. Articles help your customers find answers and can be used by bots to respond automatically."}
              </p>
              {!filterState.searchTerm && (
                <PrimaryBlueButton
                  label="Create First Article"
                  onClick={() => {
                    window.location.href = "/kb/articles/create";
                  }}
                />
              )}
            </div>
          )}

          {/* Article Count Footer */}
          {articles.length > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-t text-xs text-slate-500 rounded-b-lg">
              {articles.length} article{articles.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>

      {showFilterModal && (
        <FilterModal
          topics={topics}
          tags={tags}
          adminUsers={adminUsers}
          onApply={(filterState) => {
            setFilterState(filterState);
            setShowFilterModal(false);
          }}
          onClose={() => setShowFilterModal(false)}
          filterState={filterState}
        />
      )}
    </>
  );
}
